// backgroundPatterns/toBackgroundCss.js
//
// Turn a pattern (id + scale + fg/bg) into:
//   - a CSS `background-image` value: url("data:image/svg+xml,...")
//   - and back: parse such a value into a descriptor { id, scale, fg, bg, weight }
//
// The generated CSS string is what gets stored in the existing `bg_wallpaper`
// slot (currentBg === 3, via setBgGradient) — no new redux field, no contract
// change. The data-URI is self-describing: it carries a `data-iyuga-pattern`
// marker so `parsePatternCss` can recover the descriptor and re-open the editor
// pre-filled.

import {
  generatePatternSvg,
  normParams,
  PATTERN_BY_ID,
  SCALE_MIN,
  SCALE_MAX,
  DEFAULT_BG,
  isSafeColor,
  safeColor,
} from "./patternGenerators";

// Re-export the shared color validator so consumers/tests can import it here.
export { isSafeColor };

// Minimal, correct encoding for embedding raw SVG in a data-URI inside url("...").
// We keep most characters literal (smaller, readable) and only escape the ones
// that actually break CSS url() / data-URI parsing.
export const encodeSvg = (svg) =>
  svg
    .replace(/\n/g, "")
    .replace(/"/g, "'") // double quotes -> single (we wrap in double-quoted url)
    .replace(/%/g, "%25")
    .replace(/#/g, "%23")
    .replace(/</g, "%3C")
    .replace(/>/g, "%3E")
    .replace(/&/g, "%26")
    .replace(/{/g, "%7B")
    .replace(/}/g, "%7D");

// THE one way to turn generated SVG markup into something a CSS `url("…")` or an
// SVG `<image href>` can actually consume.
//
// `generatePatternSvg` returns RAW `<svg …>` markup — NOT a data-URI, and NOT
// safe to interpolate into `url("…")`: the markup's own double quotes close the
// url string early and the whole declaration is dropped by the CSS parser (the
// segment then renders a broken tile). Every surface that paints a generated
// pattern MUST route through here — never `url("${svg}")`.
export const svgToDataUri = (svg) =>
  typeof svg === "string" && svg ? `data:image/svg+xml,${encodeSvg(svg)}` : "";

// Build the CSS `background` value. The data-URI tiles (background-repeat
// handled by the consuming style); we also stack a solid `bg` color underneath
// as a fallback so partial transparency never shows the page through.
export const buildPatternCss = ({ id, scale, fg, bg, weight, colors } = {}) => {
  // PATTERN_BG_ENABLED data-shape (backward-compatible): prefer the ordered
  // `colors` list [line-art, ...reserved, background] when supplied (index 0 = art,
  // last = background); otherwise fall back to the legacy {fg,bg} pair. Every
  // existing caller passes fg/bg and is therefore byte-identical.
  const hasColors = Array.isArray(colors) && colors.length > 0;
  const art = hasColors ? colors[0] : fg;
  const back = hasColors ? colors[colors.length - 1] : bg;
  const params = normParams({ scale, fg: art, bg: back, weight });
  const svg = generatePatternSvg(id, params);
  if (!svg) return "";
  const url = `url("data:image/svg+xml,${encodeSvg(svg)}")`;
  // `repeat` keyword + solid bg underneath: valid in the `background` shorthand
  // and renders verbatim everywhere bg_wallpaper is consumed. `params.bg` is
  // interpolated RAW into the CSS string (it does not pass through encodeSvg),
  // so validate it against the safe-color allow-list to prevent CSS injection;
  // fall back to the default bg on anything suspicious.
  // `params.bg` is already sanitized by normParams, but guard again here since
  // this value is interpolated RAW into the CSS string (it never passes through
  // encodeSvg) — defense in depth against CSS injection.
  const safeBg = safeColor(params.bg, DEFAULT_BG);
  return `${url} repeat, ${safeBg}`;
};

// True when a stored bg_wallpaper value is one of our generated patterns.
export const isPatternCss = (value) =>
  typeof value === "string" &&
  value.includes("data:image/svg+xml") &&
  value.includes("data-iyuga-pattern");

// Recover a descriptor from a stored pattern CSS string, or null if it isn't one.
// Lets the editor re-open pre-filled with the saved id/scale/colors.
export const parsePatternCss = (value) => {
  if (!isPatternCss(value)) return null;
  try {
    const id = (value.match(/data-iyuga-pattern=['"]([a-zA-Z]+)['"]/) || [])[1];
    if (!id || !PATTERN_BY_ID[id]) return null;
    // width may appear as width="N" (raw SVG) or width='N' (encoded data-URI
    // where encodeSvg turned " into '). Match either quote.
    const wh = value.match(/width=['"](\d+(?:\.\d+)?)['"]/);
    const scale = wh ? Number(wh[1]) : undefined;
    // colors: first two fill/stroke colors found (bg rect is first, then motif).
    // In the encoded data-URI a '#' is escaped to '%23' and quotes are single,
    // so accept both the raw and the URL-encoded hex form.
    const colors = [];
    // Restrict to hex forms only (raw `#` or url-encoded `%23`). The generators
    // only ever emit hex colors (or `none`), so the previous over-permissive
    // `[a-zA-Z]+` branch was unnecessary and let arbitrary words through.
    const colorRe = /(?:fill|stroke)=['"]((?:#|%23)[0-9a-fA-F]{3,8})['"]/g;
    let m;
    while ((m = colorRe.exec(value)) && colors.length < 4) {
      if (m[1] !== "none") {
        // normalise %23 -> # so callers always see a plain hex string.
        colors.push(decodeURIComponent(m[1]).replace(/^%23/i, "#"));
      }
    }
    // The AUTHORITATIVE background is the composited layer in the CSS shorthand
    // tail — `url("...") repeat, <bg>` — written RAW by buildPatternCss (never
    // through encodeSvg). Read it directly so an explicit `transparent` background
    // round-trips faithfully (the hex-only regex above never matches the transparent
    // bgRect fill). PATTERN_BG_ENABLED. Falls back to the first recovered fill (the
    // bg rect) for any legacy value missing a parseable tail.
    const bgLayer = (value.match(
      /repeat,\s*(transparent|(?:#|%23)[0-9a-fA-F]{3,8}|(?:rgb|rgba|hsl|hsla)\([0-9.,%\s/]+\))\s*$/i
    ) || [])[1];
    const bg = bgLayer
      ? bgLayer.toLowerCase() === "transparent"
        ? "transparent"
        : decodeURIComponent(bgLayer).replace(/^%23/i, "#")
      : colors[0];
    // Line-art (ink) = the first recovered fill/stroke that is not the background.
    // For a transparent bg the bgRect fill isn't hex-matched, so colors[0] IS the ink.
    const fg =
      colors.find((c) => String(c).toLowerCase() !== String(bg).toLowerCase()) ||
      colors[0] ||
      colors[1];
    // Ordered colors list per the pattern-background LAW: [line-art, background].
    // Migration-free adapter: legacy values (no `colors` persisted) are recovered
    // here as [fg, bg], so every reader sees the ordered pair regardless of vintage.
    const patternColors = [fg, bg].filter((c) => c != null);
    const clampedScale =
      scale == null ? undefined : Math.min(SCALE_MAX, Math.max(SCALE_MIN, scale));
    return { id, scale: clampedScale, fg, bg, colors: patternColors };
  } catch {
    return null;
  }
};
